import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import * as api from 'da-pcc-frontend-accountmanagement-api';
import useRouter from 'use-react-router';

// Tile icons
import UsersImage from 'da-frontend-shared-ui-components/lib/icons/Users';
import MyAccountImage from 'da-frontend-shared-ui-components/lib/icons/Settings';
import AccountDetailImage from 'da-frontend-shared-ui-components/lib/icons/List';
import GroupsImage from 'da-frontend-shared-ui-components/lib/icons/SitePin';
import CustomersImage from 'da-frontend-shared-ui-components/lib/icons/BuildingSmall';
import DealersImage from 'da-frontend-shared-ui-components/lib/icons/BuildingLarge';
import CustomerRequestImage from 'da-frontend-shared-ui-components/lib/icons/Mail';

import Tiles, { Tile } from './Tiles';
import AccountContext from '../Context/AccountContext';

import { useAccountNotifications } from '../Context/AccountNotificationContext';

const customers = new Set([
  api.AccountTypeEnum.Consumer,
  api.AccountTypeEnum.Commercial]);

/**
 * Renders 'My Account' header area
 */
const MyAccountTiles = () => {
  const { t } = useTranslation();
  const tnav = r => t(`navigation.${r}`);

  const [{ account, accountPermissions }] = useContext(AccountContext);
  const route = r => `/${account.id}${r}`;

  // console.log("%c[ Account ]%o", "color: white; background: black", account);

  const canViewUsers =
    accountPermissions.has(api.AccountPermission.CanViewUsers) ||
    accountPermissions.has(api.AccountPermission.CanManageUsers);

  const UsersTile = () => {
    const [{ numPendingUsers }] = useAccountNotifications();

    return (
      <Tile icon={<UsersImage />} to={route`/My/Account/Users`} bubbleValue={numPendingUsers}>
        {tnav`Users`}
      </Tile>
    )
  }

  return (
    <Tiles>
      <Tile icon={<AccountDetailImage />} to={route`/My/Account/Detail`}>{tnav`Detail`}</Tile>
      {canViewUsers && <UsersTile />}
      <Tile icon={<GroupsImage />} to={route`/My/Account/Groups`} isEnabled={account.isActive}>
        {tnav`Groups`}
      </Tile>
    </Tiles>
  );
}

/** Renders 'Distributor' header area */
const DistributorTiles = () => {
  const { t } = useTranslation();
  const tnav = r => t(`navigation.${r}`);

  const [{ account, accountPermissions }] = useContext(AccountContext);
  const route = r => `/${account.id}${r}`;

  const canViewDealers =
    accountPermissions.has(api.AccountPermission.CanViewDealerAccounts) ||
    accountPermissions.has(api.AccountPermission.CanManageDealerAccounts);

  const DealerTile = () => {
    const [{ numPendingDealers }] = useAccountNotifications();

    return (
      <Tile icon={<DealersImage />} to={route`/Dealers`} bubbleValue={numPendingDealers} isEnabled={account.isActive}>
        {tnav`Dealer Accounts`}
      </Tile>
    )
  };

  return (
    <Tiles>
      <Tile icon={<MyAccountImage />} to={route`/My`}>{tnav`My Account`}</Tile>
      <Tile icon={<CustomersImage />} to={route`/Customers`} isEnabled={account.isActive}>
        {tnav`Customers`}
      </Tile>
      {canViewDealers && <DealerTile />}
    </Tiles>
  );
}

/** Renders 'Dealer' header area */
const DealerTiles = () => {
  const { t } = useTranslation();
  const tnav = r => t(`navigation.${r}`);

  const [{ account }] = useContext(AccountContext);
  const [{ numPendingConsumers }] = useAccountNotifications();

  const route = r => `/${account.id}${r}`;

  return (
    <Tiles>
      <Tile icon={<MyAccountImage />} to={route`/My`}>{tnav`My Account`}</Tile>
      <Tile icon={<CustomersImage />} to={route`/Customers`} isEnabled={account.isActive}>{tnav`Customers`}</Tile>
      <Tile icon={<CustomerRequestImage />} to={route`/ConsumerRequests`} isEnabled={account.isActive} bubbleValue={numPendingConsumers}>{tnav`Open Consumer Requests`}</Tile>
    </Tiles>
  );
}

/**
 * Root application navigation
 */
const Root = () => {
  const { match } = useRouter();
  const [account] = useContext(AccountContext);

  const ownAccount = match.params["my"] || false;
  const inDistributor = account.account.type === api.AccountTypeEnum.Distributor;
  const inDealer = account.account.type === api.AccountTypeEnum.Dealer;
  const inMyAccount = ownAccount || customers.has(account.account.type);

  return (
    <>
      {
        // My Account menu
        inMyAccount ? <MyAccountTiles /> :

          // Distributor Menu
          inDistributor ? <DistributorTiles /> :

            // Dealer menu
            inDealer ? <DealerTiles /> :

              // Nothing
              null
      }
    </>
  )
};


export default Root;

export {
  MyAccountTiles
}